// =============================================================================
// RescheduleModal — pick a new date, with an optional override + reason
// =============================================================================
// Shared by the Order Detail page and both operations calendars for both the
// click-to-reschedule flow and the override-after-a-rejected-drop flow. The
// server enforces route/capacity/lead-time rules; when it rejects (409) the
// caller reopens this with `error` set and `forceOverride` so the user can
// supply a reason and force it through.

import { useState } from 'react';
import { Modal } from '../../components/Modal';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';

export interface RescheduleSubmit {
  date: string; // ISO yyyy-mm-dd
  override: boolean;
  reason: string;
}

export function RescheduleModal({
  title,
  subtitle,
  initialDate,
  minDate,
  busy = false,
  error = null,
  forceOverride = false,
  onClose,
  onSubmit,
}: {
  title: string;
  subtitle?: string;
  initialDate: string;
  /** Earliest selectable date (ISO) — defaults to today. */
  minDate?: string;
  busy?: boolean;
  error?: string | null;
  /** Start with override enabled (e.g. reopened after a rejected drag-drop). */
  forceOverride?: boolean;
  onClose: () => void;
  onSubmit: (args: RescheduleSubmit) => void;
}): JSX.Element {
  const [date, setDate] = useState(initialDate);
  const [override, setOverride] = useState(forceOverride);
  const [reason, setReason] = useState('');

  const reasonMissing = override && !reason.trim();
  const canSubmit = !!date && !reasonMissing && !busy;

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit({ date, override, reason: reason.trim() })}
            loading={busy}
            disabled={!canSubmit}
          >
            Reschedule
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        <Input
          label="New date"
          type="date"
          value={date}
          min={minDate}
          onChange={(e) => setDate(e.target.value)}
          autoFocus
        />

        {error && (
          <div className="rounded border border-warning/40 bg-warning-soft/50 text-ink-2 text-xs px-3 py-2">
            {error}
          </div>
        )}

        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={override}
            onChange={(e) => setOverride(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="text-ink">Override scheduling rules</span>
            <span className="block text-[11px] text-ink-3">
              Force the date through even if it breaks the route day, daily capacity, or lead time.
              Logged with your name.
            </span>
          </span>
        </label>

        {override && (
          <Input
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Customer requested earlier delivery; factory will prioritise"
            error={reasonMissing ? 'A reason is required to override.' : undefined}
          />
        )}
      </div>
    </Modal>
  );
}
